<template>
  <div v-if="visible" class="px-4 pt-4 sm:px-6 lg:px-8">
    <app-alert
      tone="warning"
      variant="soft"
      :icon="passkeyFirst ? mdiFingerprint : mdiShieldAlert"
      :title="passkeyFirst ? t('two_factor.prompt_passkey_title') : t('two_factor.prompt_title')"
    >
      {{ passkeyFirst ? t('two_factor.prompt_passkey_description') : t('two_factor.prompt_description') }}
      <!-- Soft secondary nudge: an authenticator app / SMS code as a backup
           factor, shown only when neither is set up yet. -->
      <p v-if="showBackupHint" class="mt-2 text-sm opacity-80" data-test="twofa-prompt-backup-hint">
        {{ t('two_factor.prompt_consider_backup') }}
      </p>
      <template #actions>
        <!-- Browser supports passkeys → lead with creating one. -->
        <app-button
          v-if="passkeyFirst"
          :icon="mdiFingerprint"
          :label="t('two_factor.prompt_create_passkey')"
          data-test="twofa-prompt-create-passkey"
          color="success"
          small
          :isLoading="isRegistering"
          @click="createPasskey"
        />
        <app-button
          :icon="passkeyFirst ? undefined : mdiArrowRight"
          :label="passkeyFirst ? t('two_factor.prompt_choose_another') : t('two_factor.prompt_setup')"
          data-test="twofa-prompt-setup"
          :color="passkeyFirst ? 'light' : 'success'"
          :outline="passkeyFirst"
          small
          @click="openProfile"
        />
        <app-button
          :label="t('two_factor.remind_later')"
          color="light"
          outline
          small
          @click="dismissForNow"
        />
      </template>
    </app-alert>
  </div>
</template>

<script setup>
  import { mdiArrowRight, mdiFingerprint, mdiShieldAlert } from '@mdi/js';
  import { computed, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute, useRouter } from 'vue-router';

  import AppAlert from '@/components/ui/feedback/AppAlert.vue';
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import AuthRepository from '@/repositories/AuthRepository';
  import { useAuthStore } from '@/stores/useAuthStore';
  import { useWebAuthn } from '@/composables/useWebAuthn';
  import { rememberPasskey } from '@/composables/usePasskeyHint';
  import { useNotification } from '@/composables/useNotification';

  const REMINDER_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

  const { t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const authStore = useAuthStore();
  const { notifySuccess, notifyError } = useNotification();
  const { isSupported, register } = useWebAuthn();
  const visible = ref(false);
  const isRegistering = ref(false);
  // The latest server-side 2FA status; null until the first fetch resolves.
  const status = ref(null);
  // Whether `status` reflects a completed fetch for the current login. Gates
  // `applyVisibility()` so the prompt never flips to visible mid-render — it
  // only appears once, right after the one fetch below resolves.
  const hasLoadedStatus = ref(false);

  const factorFlags = computed(() => {
    const s = status.value;
    return {
      hasPasskey: (s?.webauthn?.credentials_count || 0) > 0,
      hasTotp: Boolean(s?.totp?.confirmed),
      hasSms: Boolean(s?.sms?.available && s?.sms?.phone_verified && s?.sms?.enabled),
      hasEmail: Boolean(s?.email?.available && s?.email?.enabled),
    };
  });

  // What to nudge the user towards, in line with the preferred-factor order
  // (passkey > authenticator/SMS > email):
  //  - 'setup'   : no second factor at all → strong "secure your account".
  //  - 'passkey' : has a code-based factor but no passkey on a capable browser
  //                → "add a passkey" (e.g. right after an email-2FA login).
  //  - null      : already has a passkey, or nothing actionable.
  const recommendation = computed(() => {
    if (!status.value) return null;
    const { hasPasskey, hasTotp, hasSms, hasEmail } = factorFlags.value;
    const anyFactor = hasPasskey || hasTotp || hasSms || hasEmail;
    if (!anyFactor) return 'setup';
    if (isSupported.value && !hasPasskey) return 'passkey';
    return null;
  });

  // Lead with the passkey CTA for the passkey upgrade, and for a fresh setup
  // whenever the browser supports passkeys.
  const passkeyFirst = computed(
    () => recommendation.value === 'passkey' || (recommendation.value === 'setup' && isSupported.value)
  );

  // Soft suggestion to add an authenticator app / SMS as a backup factor,
  // surfaced inside an already-visible prompt when neither is configured.
  const showBackupHint = computed(
    () => recommendation.value !== null && !factorFlags.value.hasTotp && !factorFlags.value.hasSms
  );

  const storageKey = () => {
    const identity = authStore.id || authStore.email || 'anonymous';
    return `profiteia:two_factor_prompt_dismissed_until:${identity}`;
  };

  const isReminderDismissed = () => {
    try {
      const dismissedUntil = Number(localStorage.getItem(storageKey()) || 0);
      return Number.isFinite(dismissedUntil) && dismissedUntil > Date.now();
    } catch {
      return false;
    }
  };

  // Synchronous, no network: derives `visible` from already-cached `status`.
  // Run on every route change so navigation never waits on a fetch — that
  // wait is what used to pop the banner into the page after it had already
  // rendered, shifting everything below it.
  const applyVisibility = () => {
    if (!authStore.isAuthenticated || isReminderDismissed()) {
      visible.value = false;
      return;
    }
    if (!hasLoadedStatus.value) return;
    // The full "set up 2FA" nudge is redundant on the profile page (the 2FA
    // settings card lives there); the passkey-upgrade nudge stays visible
    // even on /profile so it appears right after a code-based login.
    if (recommendation.value === 'setup' && route.path === '/profile') {
      visible.value = false;
      return;
    }
    visible.value = recommendation.value !== null;
  };

  // Fetches the status at most once per login. The settings page (/profile)
  // is the only place 2FA/passkeys get changed, so leaving it is the one
  // navigation worth a refetch; every other route change just re-derives
  // visibility from the cached status.
  const loadStatus = async () => {
    if (!authStore.isAuthenticated || isReminderDismissed()) {
      applyVisibility();
      return;
    }
    if (hasLoadedStatus.value) {
      applyVisibility();
      return;
    }

    try {
      const response = await AuthRepository.twoFactorStatus();
      status.value = response.data;
    } catch {
      status.value = null;
    } finally {
      hasLoadedStatus.value = true;
      applyVisibility();
    }
  };

  const dismissForNow = () => {
    try {
      localStorage.setItem(storageKey(), String(Date.now() + REMINDER_INTERVAL_MS));
    } catch {
      // Ignore storage failures; the prompt is still dismissed for this render.
    }
    visible.value = false;
  };

  const openProfile = () => {
    visible.value = false;
    router.push('/profile');
  };

  const createPasskey = async () => {
    if (isRegistering.value) return;
    isRegistering.value = true;
    try {
      await register();
      const name = [authStore.firstname, authStore.lastname].filter(Boolean).join(' ').trim();
      rememberPasskey({ email: authStore.email, name });
      notifySuccess(t('passkey.register_success'));
      // A passkey now satisfies the second-factor requirement → hide the nudge.
      visible.value = false;
    } catch (err) {
      if (err?.name !== 'NotAllowedError') {
        notifyError(t('passkey.register_error'));
      }
    } finally {
      isRegistering.value = false;
    }
  };

  onMounted(loadStatus);

  watch(
    () => [authStore.isAuthenticated, authStore.id, route.path],
    ([, newId, newPath], [, oldId, oldPath]) => {
      const leftProfile = oldPath === '/profile' && newPath !== '/profile';
      if (newId !== oldId || leftProfile) {
        hasLoadedStatus.value = false;
        status.value = null;
      }
      loadStatus();
    }
  );
</script>
